"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import {
  Icon20CancelCircleOutline,
  Icon28PlaceOutline,
  Icon24Rocket,
  Icon20SparkleOutline,
} from "@linqx/icons/web";
import type { ComponentType } from "react";
import { Language } from "@linqx/i18n";
import { Reveal } from "@/components/motion/scroll-fx";

interface Stat {
  value: number;
  suffix: string;
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
}

const STATS: Stat[] = [
  {
    value: 0,
    suffix: "",
    labelKey: "landing_stats_small_talk_label",
    icon: Icon20CancelCircleOutline,
  },
  {
    value: 100,
    suffix: "%",
    labelKey: "landing_stats_irl_label",
    icon: Icon28PlaceOutline,
  },
  {
    value: 3,
    suffix: Language.getKey("landing_stats_steps_suffix"),
    labelKey: "landing_stats_steps_label",
    icon: Icon24Rocket,
  },
  {
    value: 12,
    suffix: "+",
    labelKey: "landing_stats_vibes_label",
    icon: Icon20SparkleOutline,
  },
];

/** Animated number that counts up to {@link value} when scrolled into view. */
function CountUp({ value, suffix }: { value: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span
      ref={ref}
      className="font-heading text-5xl md:text-6xl font-extrabold text-brand-900"
    >
      {display}
      {suffix}
    </span>
  );
}

export function Stats() {
  return (
    <section className="relative overflow-hidden bg-background py-28 md:py-36">
      <div className="container relative z-10 mx-auto px-4">
        <Reveal direction="up" className="mx-auto mb-16 max-w-2xl text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-brand-900">
            {Language.getKey("landing_stats_title_prefix")}
            <span className="text-brand-500">
              {Language.getKey("landing_stats_title_highlight")}
            </span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {Language.getKey("landing_stats_subtitle")}
          </p>
        </Reveal>

        <div className="grid grid-cols-2 gap-6 md:grid-cols-4 md:gap-8">
          {STATS.map((stat, i) => (
            <Reveal
              key={stat.labelKey}
              direction={i % 2 === 0 ? "left" : "right"}
              distance={70}
              delay={i * 0.03}
              className="group rounded-3xl border border-brand-100 bg-white/70 p-6 text-center backdrop-blur-sm transition-colors hover:border-brand-300 md:p-8"
            >
              <motion.div
                whileHover={{ scale: 1.12, rotate: [0, -6, 6, 0] }}
                transition={{ duration: 0.4 }}
                className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-lg shadow-brand-500/25 ${
                  i === 0
                    ? "bg-gradient-to-br from-brand-900 to-brand-500"
                    : "bg-brand-900"
                }`}
              >
                <stat.icon className="h-5 w-5" />
              </motion.div>
              <CountUp value={stat.value} suffix={stat.suffix} />
              <p className="mt-2 text-sm font-medium text-brand-600">
                {Language.getKey(stat.labelKey)}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
